<script lang="ts">
    import { silentStore } from "../../Stores/MediaStore";

    import { gameManager } from "../../Phaser/Game/GameManager";
    import { chatVisibilityStore } from "../../Stores/ChatStore";
    import {
        inExternalServiceStore,
        myCameraStore,
        myMicrophoneStore,
        proximityMeetingStore,
    } from "../../Stores/MyMediaStore";
    import type { RightMenuItem } from "../../Stores/MenuStore";
    import { mediaSettingsOpenStore, rightActionBarMenuItems } from "../../Stores/MenuStore";
    import { IconChevronUp } from "../Icons";
    import { hideActionBarStoreBecauseOfChatBar } from "../../Chat/ChatSidebarWidthStore";
    import { screenSharingAvailableStore } from "../../Stores/ScreenSharingStore";
    import { isInRemoteConversation } from "../../Stores/StreamableCollectionStore";
    import MediaSettingsList from "./MediaSettingsList/MediaSettingsList.svelte";
    import CameraMenuItem from "./MenuIcons/CameraMenuItem.svelte";
    import MicrophoneMenuItem from "./MenuIcons/MicrophoneMenuItem.svelte";
    import ScreenSharingMenuItem from "./MenuIcons/ScreenSharingMenuItem.svelte";
    import ChatMenuItem from "./MenuIcons/ChatMenuItem.svelte";
    import UserListMenuItem from "./MenuIcons/UserListMenuItem.svelte";
    import ResponsiveActionBar from "./ResponsiveActionBar.svelte";
    import ProfileMenu from "./MenuIcons/ProfileMenu.svelte";
    import VisibilityChecker from "./VisibilityChecker.svelte";
    import ContextualMenuItems from "./MenuIcons/ContextualMenuItems.svelte";
    import CloseChatMenuItem from "./MenuIcons/CloseChatMenuItem.svelte";
    import SilentBlock from "./SilentBlock.svelte";
    import PictureInPictureMenuItem from "./MenuIcons/PictureInPictureMenuItem.svelte";

    let rightDiv: HTMLDivElement | undefined = $state();
    let smallArrowVisible = true;
    let actionBarWidth: number = $state(0);

    const gameScene = gameManager.getCurrentGameScene();
    const showChatButton = gameScene.room.isChatEnabled;
    const showUserListButton = gameScene.room.isChatOnlineListEnabled;

    let isSmallScreen = $derived(actionBarWidth < 640);

    function onMenuItemVisibilityChange(isVisible: boolean, button: RightMenuItem) {
        button.fallsInBurgerMenuStore.set(!isVisible);
    }
</script>

{#if !$hideActionBarStoreBecauseOfChatBar}
    <ResponsiveActionBar bind:rightDiv bind:actionBarWidth>
        {#snippet left()}
            <div class="justify-start flex-none">
                <div class="flex relative transition-all duration-150 z-[2]" data-testid="chat-action">
                    {#if !$chatVisibilityStore}
                        <ChatMenuItem chatEnabledInAdmin={showChatButton} last={isSmallScreen ? true : undefined} />
                        {#if !isSmallScreen && showUserListButton}
                            <UserListMenuItem state={showUserListButton ? "normal" : "disabled"} />
                        {/if}
                    {:else}
                        <CloseChatMenuItem />
                    {/if}
                </div>
            </div>
        {/snippet}

        {#snippet center()}
            <div
                class="@xxs/actions:justify-center justify-end main-action pointer-events-auto min-w-32 @sm/actions:min-w-[192px]"
            >
                <div
                    class="flex justify-center relative gap-1 @md/actions:gap-2 @xl/actions:gap-4 z-[1] mx-1 @md/actions:mx-2 @xl/actions:mx-4"
                >
                    <div class="hidden @sm/actions:flex items-center">
                        <ContextualMenuItems />
                    </div>

                    <div>
                        <!-- ACTION WRAPPER : CAM & MIC -->
                        <div class="group/hardware flex items-center relative">
                            {#if !$inExternalServiceStore && $proximityMeetingStore && $myMicrophoneStore}
                                <MicrophoneMenuItem />
                            {/if}

                            {#if smallArrowVisible}
                                <div
                                    class="absolute h-3 mobile:h-6 w-7 rounded-b mobile:rounded-t bg-contrast/80 backdrop-blur start-[2.86rem] m-auto p-1 z-10 transition-all -bottom-3 hidden opacity-0 sm:block mobile:-top-12 mobile:block {$inExternalServiceStore
                                        ? ''
                                        : 'mobile:opacity-100'}
                                    {$mediaSettingsOpenStore ? 'opacity-100' : 'group-hover/hardware:opacity-100'}"
                                >
                                    <!-- svelte-ignore a11y_click_events_have_key_events -->
                                    <!-- svelte-ignore a11y_no_static_element_interactions -->
                                    <div
                                        data-testid="media-settings-toggle-button"
                                        class="absolute bottom-1 start-0 end-0 m-auto hover:bg-white/10 h-5 w-5 flex items-center justify-center rounded-sm mobile:rotate-180"
                                        onclick={(event) => {
                                            event.stopPropagation();
                                            event.preventDefault();
                                            mediaSettingsOpenStore.set(!$mediaSettingsOpenStore);
                                        }}
                                    >
                                        <IconChevronUp
                                            stroke="2"
                                            class="aspect-square transition-all select-none {$mediaSettingsOpenStore
                                                ? ''
                                                : 'rotate-180'}"
                                        />
                                    </div>
                                </div>
                            {/if}
                            {#if $mediaSettingsOpenStore}
                                <MediaSettingsList onclose={() => mediaSettingsOpenStore.set(false)} />
                            {/if}
                            <!-- NAV : CAMERA START -->
                            {#if !$inExternalServiceStore && $myCameraStore}
                                <CameraMenuItem />
                            {/if}
                            <!-- NAV : CAMERA END -->

                            <!-- NAV : SCREENSHARING START -->
                            {#if $screenSharingAvailableStore}
                                <ScreenSharingMenuItem />
                            {/if}
                            {#if $isInRemoteConversation}
                                <PictureInPictureMenuItem />
                            {/if}
                            <!-- NAV : SCREENSHARING END -->
                        </div>
                    </div>
                </div>
                <!-- NAV : SILENT BLOCK -->
                {#if $silentStore}
                    <SilentBlock />
                {/if}
            </div>
        {/snippet}

        {#snippet right()}
            <div id="action-wrapper" class="flex flex-1 justify-end gap-1 @md/actions:gap-2 @xl/actions:gap-4">
                <div class="flex flex-row flex-0 gap-0">
                    {#if rightDiv}
                        {#each $rightActionBarMenuItems as button, index (button.id)}
                            {@const ButtonComponent = button.component}
                            <VisibilityChecker
                                parent={rightDiv}
                                onVisibilityChange={(visibility) => onMenuItemVisibilityChange(visibility, button)}
                            >
                                <ButtonComponent
                                    {...button.props}
                                    classList={button.props.last && index !== $rightActionBarMenuItems.length - 1
                                        ? "me-1 @md/actions:me-2 @xl/actions:me-4"
                                        : ""}
                                />
                            </VisibilityChecker>
                        {/each}
                    {/if}
                </div>

                <div class="flex justify-end gap-1 md:gap-2 xl:gap-4">
                    <ProfileMenu />
                </div>
            </div>
        {/snippet}
    </ResponsiveActionBar>
{/if}
